import dayjs from 'dayjs'

import { useMemo } from 'react'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useAssetsData } from './useAssetsData'
import { useAssetPairPrice } from 'modules/blockChain/hooks/useAssetPairPrice'

import { weiToNum, weiToStr } from 'modules/blockChain/utils/parseWei'
import {
  DEFAULT_DECIMALS,
  getERCAssetByAddress,
} from 'modules/pools/constants/poolAssets'
import type { Loan } from '../types/Loan'

type Args = {
  loan: Loan
}

const PERIODS_IN_YEAR = 12 * 24 * 30

export function useLoanCalcs({ loan }: Args) {
  const { chainId } = useWeb3()
  const { getAssetDecimals } = useAssetsData()

  const { ERC20Address } = loan

  const borrowedAsset = getERCAssetByAddress(ERC20Address, chainId)
  const borrowedAssetDecimals = getAssetDecimals(borrowedAsset)
  const borrowedAssetPriceData = useAssetPairPrice(borrowedAsset, 'USD')

  return useMemo(() => {
    const {
      principal: principalWei,
      interestRate: interestRateWei,
      issueDate,
      maturityDate,
      totalPaymentsValue,
      paymentComplete,
      ltv,
      status,
    } = loan

    const borrowedAssetPrice =
      borrowedAssetPriceData &&
      weiToNum(borrowedAssetPriceData.price, borrowedAssetPriceData.decimals)

    const maturityDateNum = Number(maturityDate)
    const daysLeftToMaturity = dayjs
      .unix(maturityDateNum)
      .diff(Date.now(), 'day')
    const issueDateNum = Number(issueDate)
    const loanTermInDays = dayjs
      .unix(maturityDateNum)
      .diff(dayjs.unix(issueDateNum), 'day')
    const interestRateAnnual = interestRateWei.mul(PERIODS_IN_YEAR)
    const interestRate = weiToNum(
      interestRateAnnual.div(365).mul(loanTermInDays),
      DEFAULT_DECIMALS,
    )

    const principal = weiToNum(principalWei, borrowedAssetDecimals)
    // If loan is repaid, use full debt value
    const totalDebtWei =
      status === 4 || status === 5
        ? totalPaymentsValue
        : totalPaymentsValue.sub(paymentComplete)
    const totalDebt = weiToNum(totalDebtWei, borrowedAssetDecimals)
    const interest = weiToStr(
      totalPaymentsValue.sub(principalWei),
      borrowedAssetDecimals,
    )
    const totalDebtUSD = borrowedAssetPrice && totalDebt * borrowedAssetPrice

    return {
      ltv: weiToNum(ltv, DEFAULT_DECIMALS),
      interestRate,
      principal,
      interest,
      totalDebt,
      totalDebtUSD,
      issueDate: issueDateNum,
      maturityDate: maturityDateNum,
      daysLeftToMaturity,
      borrowedAsset,
      borrowedAssetDecimals,
    }
  }, [borrowedAsset, borrowedAssetDecimals, borrowedAssetPriceData, loan])
}
